import { Account, makeAccount } from './data.ts';
import { Storage } from './storage.ts';

export type AddAccountParams = {
  attendeeUid: string;
  eventCode: string;
  eventLogo: string;
  name: string;
  email: string;
  phoneNumber: string;
  regDate: Date;
  expiredDate: Date | null;
};

export type MultiAccountLogic = {
  addAccount: (accountData: AddAccountParams) => void;
  isExistsAccount: (params: { attendeeUid: string; eventCode: string }) => boolean;
  findIndex: (params: { attendeeUid: string; eventCode: string }) => number;
  findIndexByEventCode: (eventCode: string) => number;
  getList: () => Account[];
  setList: (list: Account[]) => void;
  setCurrentAccount: (index: number) => void;
  getCurrentAccount: () => Account | null;
  getCurrentIndex: () => number;
  onChangeCurrentIndex: (callback: (index: number) => void) => void;
};

export function init(storage: Storage): MultiAccountLogic {
  const subscriptionsOnChangeCurrentIndex: ((index: number) => void)[] = [];
  return {
    addAccount,
    isExistsAccount,
    findIndex,
    findIndexByEventCode,
    getList,
    setCurrentAccount,
    getCurrentAccount,
    getCurrentIndex,
    onChangeCurrentIndex,
    setList,
  };

  function addAccount(params: {
    attendeeUid: string;
    eventCode: string;
    eventLogo: string;
    name: string;
    email: string;
    phoneNumber: string;
    regDate: Date;
    expiredDate: Date | null;
  }): void {
    const account = makeAccount(params);
    storage.setList([account, ...getList()]);
  }

  function getCurrentAccount(): Account | null {
    return getList()[storage.getCurrentIndex()] || null;
  }

  function getCurrentIndex(): number {
    return storage.getCurrentIndex();
  }

  function setCurrentAccount(index: number): void {
    storage.setCurrentIndex(index);
    subscriptionsOnChangeCurrentIndex.forEach((callback) => callback(index));
  }

  function onChangeCurrentIndex(callback: (index: number) => void): void {
    subscriptionsOnChangeCurrentIndex.push(callback);
  }

  function getList(): Account[] {
    return storage.getList();
  }

  function setList(list: Account[]): void {
    storage.setList(list);
  }

  function findIndex({ attendeeUid, eventCode }: { attendeeUid: string; eventCode: string }): number {
    return getList().findIndex((account) => account.attendeeUid === attendeeUid && account.eventCode === eventCode);
  }

  function findIndexByEventCode(eventCode: string): number {
    return getList().findIndex((account) => account.eventCode === eventCode);
  }

  function isExistsAccount({ attendeeUid, eventCode }: { attendeeUid: string; eventCode: string }): boolean {
    return findIndex({ attendeeUid, eventCode }) > -1;
  }
}
